/*
 * The residents of a home: adding them, keeping their details right, and recording what happens
 * to them.
 *
 * This is the first screen anyone uses at a new home, so it is written for somebody sitting with a
 * pile of paper MAR charts working through them one at a time. The order of the fields follows the
 * order they appear on a chart, and nothing is demanded that the person typing might not have in
 * front of them.
 *
 * Two things here are deliberately awkward. A second resident with the same surname has to be
 * confirmed, because that is how the wrong person gets the wrong medicine. And "has died" asks for
 * confirmation in words, because it stops their medicines and cannot be undone here.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { Resident, User } from './api.ts';
import { ApprovalDialog } from './approval.tsx';
import type { Draft, Report } from './medicine-types.ts';
import { Overlay } from './overlay.tsx';
import { PrintoutsScreen } from './printouts.tsx';
import { ResidentMedicines } from './resident-medicines.tsx';
import { TraysScreen } from './trays.tsx';

type Status = 'here' | 'hospital' | 'away' | 'left' | 'died';

type Full = {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  room: string;
  nhsNumber: string;
  gpPractice: string;
  gpPhone: string;
  allergies: string[];
  admittedOn: string | null;
  photoConsent: boolean;
  status: Status;
  statusLabel: string;
  statusNote: string;
  statusSince: string | null;
  createdBy: string;
  updatedBy: string;
};

type Record_ = {
  resident: Full;
  medicines: { live: number; controlled: number };
  history: Array<{
    kind: string;
    note: string;
    detail: Record<string, unknown>;
    happenedOn: string;
    recordedAt: string;
    by: string;
  }>;
};

const STATUS_LABEL: Record<Status, string> = {
  here: 'In the home',
  hospital: 'In hospital',
  away: 'Away',
  left: 'Has left',
  died: 'Has died',
};

const HISTORY_LABEL: Record<string, string> = {
  admitted: 'Admitted',
  hospital: 'Went into hospital',
  away: 'Went away',
  returned: 'Came back',
  'moved-room': 'Moved room',
  left: 'Left the home',
  died: 'Died',
  'details-changed': 'Details changed',
};

/* `openResidentId` opens straight onto one resident — from a reported change, say. */
export function ResidentsScreen({
  homeId, user, openResidentId,
}: { homeId: string; user: User; openResidentId?: string | null }) {
  const [residents, setResidents] = useState<Resident[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(openResidentId ?? null);

  useEffect(() => {
    if (openResidentId) setOpenId(openResidentId);
  }, [openResidentId]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [approving, setApproving] = useState(false);
  const [message, setMessage] = useState('');

  const canSetUp = user.roles.includes('pharmacy') || user.roles.includes('manage');
  const isPharmacy = user.roles.includes('pharmacy') && user.side === 'pharmacy';

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ residents: Resident[] }>(`/api/homes/${homeId}/residents`);
      setResidents(data.residents);
      // Where the work is: the pharmacy's drafts, and what the home has reported and is waiting on.
      const open = await api.get<{ reports: Report[] }>(`/api/homes/${homeId}/reports?show=open`);
      setReports(open.reports);
      if (isPharmacy) {
        const waiting = await api.get<{ drafts: Draft[] }>(`/api/homes/${homeId}/drafts`);
        setDrafts(waiting.drafts);
      }
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the residents.');
    }
  }, [homeId, isPharmacy]);

  useEffect(() => {
    void load();
  }, [load]);

  if (openId) {
    return (
      <ResidentRecord
        homeId={homeId}
        residentId={openId}
        user={user}
        onBack={() => {
          setOpenId(null);
          void load();
        }}
      />
    );
  }

  const away = (residents ?? []).filter((r) => r.status && r.status !== 'here');
  const draftsFor = (id: string) => drafts.filter((d) => d.residentId === id).length;
  const reportsFor = (id: string) => reports.filter((r) => r.residentId === id).length;
  const draftResidents = new Set(drafts.map((d) => d.residentId)).size;

  return (
    <section>
      <div className="head">
        <h1>Residents</h1>
        <div className="row-actions">
          {canSetUp ? <button type="button" onClick={() => setAdding(true)}>Add a resident</button> : null}
          <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      <p className="muted">
        Everyone whose record is open. Somebody in hospital keeps their record and their medicines, and
        comes back on the round when they do.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {message ? <p className="notice" role="status">{message}</p> : null}

      {isPharmacy && drafts.length ? (
        <div className="drafts-bar">
          <p>
            <strong>
              {drafts.length} change{drafts.length === 1 ? '' : 's'} for {draftResidents} resident{draftResidents === 1 ? '' : 's'} not on
              the home's chart yet.
            </strong>{' '}
            Approve them once the trays are checked and packed.
          </p>
          <button type="button" onClick={() => setApproving(true)}>Approve everything for this home</button>
        </div>
      ) : null}

      {residents && !residents.length ? (
        <p className="notice">
          Nobody has been added yet. {canSetUp ? 'Add the first resident to begin setting this home up.' : 'The pharmacy sets the home up.'}
        </p>
      ) : null}
      {away.length ? (
        <p className="notice">
          {away.length} resident{away.length === 1 ? ' is' : 's are'} away from the home, so they are not on the round.
        </p>
      ) : null}

      <ul className="list">
        {(residents ?? []).map((r) => (
          <li key={r.id}>
            <button type="button" className="row column" onClick={() => setOpenId(r.id)}>
              <div className="row-line">
                <span className="row-main">
                  {r.title ? `${r.title} ` : ''}{r.firstName} {r.lastName}
                  {r.preferredName ? <span className="muted"> ({r.preferredName})</span> : null}
                </span>
                <span className="chips">
                  {r.status && r.status !== 'here' ? (
                    <span className="chip warn">{STATUS_LABEL[r.status as Status] ?? r.status}</span>
                  ) : null}
                  {r.allergies?.length ? <span className="chip off">Allergies</span> : null}
                  {reportsFor(r.id) ? <span className="chip warn">Change reported</span> : null}
                  {isPharmacy && draftsFor(r.id) ? (
                    <span className="chip">{draftsFor(r.id)} draft{draftsFor(r.id) === 1 ? '' : 's'}</span>
                  ) : null}
                </span>
              </div>
              <p className="muted small">
                {r.room ? `Room ${r.room}` : 'No room recorded'}
                {r.dateOfBirth ? ` · born ${formatDate(r.dateOfBirth)}` : ''}
                {r.allergies?.length ? ` · allergic to ${r.allergies.join(', ')}` : ''}
              </p>
            </button>
          </li>
        ))}
      </ul>

      {approving ? (
        <ApprovalDialog
          homeId={homeId}
          onClose={() => setApproving(false)}
          onDone={(approved) => {
            setApproving(false);
            setMessage(`${approved} change${approved === 1 ? '' : 's'} approved. They are on the home's chart now.`);
            void load();
          }}
        />
      ) : null}

      {adding ? (
        <ResidentForm
          homeId={homeId}
          onClose={() => setAdding(false)}
          onSaved={(id) => {
            setAdding(false);
            void load();
            setOpenId(id);
          }}
        />
      ) : null}
    </section>
  );
}

/* ---------- One resident ---------- */

function ResidentRecord({
  homeId, residentId, user, onBack,
}: { homeId: string; residentId: string; user: User; onBack: () => void }) {
  const [data, setData] = useState<Record_ | null>(null);
  // Printing takes over the page: their seals, or their backup MAR.
  const [printing, setPrinting] = useState<'seals' | 'mar' | null>(null);
  const [editing, setEditing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState('');

  const canSetUp = user.roles.includes('pharmacy') || user.roles.includes('manage');
  const canChangeStatus = canSetUp || user.roles.includes('nurse') || user.roles.includes('check');

  const load = useCallback(async () => {
    try {
      setData(await api.get<Record_>(`/api/residents/${residentId}/record`));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that resident.');
    }
  }, [residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const r = data?.resident;

  if (printing === 'seals') return <TraysScreen homeId={homeId} residentId={residentId} onBack={() => setPrinting(null)} />;
  if (printing === 'mar') return <PrintoutsScreen homeId={homeId} residentId={residentId} onBack={() => setPrinting(null)} />;

  return (
    <section>
      <div className="head">
        <div>
          <h1>{r ? `${r.title ? `${r.title} ` : ''}${r.firstName} ${r.lastName}` : 'Resident'}</h1>
          {r ? (
            <p className="muted small">
              {r.room ? `Room ${r.room}` : 'No room'} · born {formatDate(r.dateOfBirth)}
              {r.preferredName ? ` · known as ${r.preferredName}` : ''}
            </p>
          ) : null}
        </div>
        <button type="button" className="quiet" onClick={onBack}>Back to residents</button>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {r && r.status !== 'here' ? (
        <p className="notice warn">
          <strong>{r.statusLabel}</strong>
          {r.statusSince ? ` since ${formatDate(r.statusSince)}` : ''}
          {r.statusNote ? ` — ${r.statusNote}` : ''}
          {r.status === 'hospital' || r.status === 'away'
            ? '. Their medicines are paused, not stopped: they come back on the round when they return.'
            : '. This record is closed and their medicines have been stopped.'}
        </p>
      ) : null}

      {r?.allergies.length ? (
        <p className="notice warn"><strong>Allergic to:</strong> {r.allergies.join(', ')}</p>
      ) : r ? (
        <p className="notice">
          No allergies recorded. That is not the same as "no known allergies" — check it against the GP record.
        </p>
      ) : null}

      {r ? (
        <dl className="facts">
          <div><dt>NHS number</dt><dd>{r.nhsNumber ? formatNhs(r.nhsNumber) : 'Not recorded'}</dd></div>
          <div><dt>GP practice</dt><dd>{r.gpPractice || 'Not recorded'}{r.gpPhone ? ` · ${r.gpPhone}` : ''}</dd></div>
          <div><dt>Admitted</dt><dd>{r.admittedOn ? formatDate(r.admittedOn) : 'Not recorded'}</dd></div>
        </dl>
      ) : null}

      <div className="row-actions">
        {canSetUp && r ? <button type="button" onClick={() => setEditing(true)}>Change their details</button> : null}
        {canChangeStatus && r && r.status !== 'died' ? (
          <button type="button" className="quiet" onClick={() => setChanging(true)}>
            Record what has happened
          </button>
        ) : null}
      </div>

      {r ? (
        <ResidentMedicines
          residentId={residentId}
          residentName={`${r.title ? `${r.title} ` : ''}${r.firstName} ${r.lastName}`}
          onPrintSeals={() => setPrinting('seals')}
          onPrintMar={() => setPrinting('mar')}
        />
      ) : null}

      <h2>History</h2>
      {data && !data.history.length ? <p className="muted">Nothing recorded yet.</p> : null}
      <ul className="list">
        {(data?.history ?? []).map((h, index) => (
          <li key={`${h.recordedAt}-${index}`}>
            <div className="row static column">
              <div className="row-line">
                <span className="row-main">{HISTORY_LABEL[h.kind] ?? h.kind}</span>
                <span className="muted small">{formatDate(h.happenedOn)}</span>
              </div>
              {h.note ? <p className="muted small">{h.note}</p> : null}
              <p className="muted small">Recorded by {h.by}</p>
            </div>
          </li>
        ))}
      </ul>

      {editing && r ? (
        <ResidentForm
          existing={r}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      ) : null}

      {changing && r ? (
        <StatusDialog
          resident={r}
          medicines={data?.medicines ?? { live: 0, controlled: 0 }}
          onClose={() => setChanging(false)}
          onDone={() => {
            setChanging(false);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

/* ---------- Adding and editing ---------- */

function ResidentForm({
  homeId,
  existing,
  onClose,
  onSaved,
}: {
  homeId?: string;
  existing?: Full;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [firstName, setFirstName] = useState(existing?.firstName ?? '');
  const [lastName, setLastName] = useState(existing?.lastName ?? '');
  const [preferredName, setPreferredName] = useState(existing?.preferredName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(existing?.dateOfBirth ?? '');
  const [room, setRoom] = useState(existing?.room ?? '');
  const [nhsNumber, setNhsNumber] = useState(existing?.nhsNumber ?? '');
  const [gpPractice, setGpPractice] = useState(existing?.gpPractice ?? '');
  const [gpPhone, setGpPhone] = useState(existing?.gpPhone ?? '');
  const [allergies, setAllergies] = useState((existing?.allergies ?? []).join(', '));
  const [admittedOn, setAdmittedOn] = useState(existing?.admittedOn ?? '');
  const [error, setError] = useState('');
  const [sameName, setSameName] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (sameNameIsFine: boolean) => {
      setBusy(true);
      setError('');
      const payload = {
        title,
        firstName,
        lastName,
        preferredName,
        dateOfBirth,
        room,
        nhsNumber,
        gpPractice,
        gpPhone,
        allergies: allergies.split(',').map((a) => a.trim()).filter(Boolean),
        admittedOn: admittedOn || null,
        sameNameIsFine,
      };
      try {
        if (existing) {
          await api.post(`/api/residents/${existing.id}`, payload);
          onSaved(existing.id);
        } else {
          const data = await api.post<{ id: string }>(`/api/homes/${homeId}/residents`, payload);
          onSaved(data.id);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && /same surname/i.test(err.message)) setSameName(true);
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [title, firstName, lastName, preferredName, dateOfBirth, room, nhsNumber, gpPractice, gpPhone,
      allergies, admittedOn, existing, homeId, onSaved],
  );

  return (
    <Overlay onClose={onClose}>
      <form
        className="card wide"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(false);
        }}
      >
        <h2>{existing ? 'Change their details' : 'Add a resident'}</h2>
        <p className="muted">
          Copy this from the paper MAR chart or the GP record. Anything you are not sure of can be left
          blank and added later — except the name and date of birth, which identify them.
        </p>

        <div className="slot-top">
          <div className="narrow">
            <label htmlFor="r-title">Title</label>
            <input id="r-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mrs" />
          </div>
          <div>
            <label htmlFor="r-first">First name</label>
            <input id="r-first" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="r-last">Surname</label>
            <input id="r-last" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>

        <label htmlFor="r-known">What they like to be called</label>
        <input id="r-known" value={preferredName} onChange={(e) => setPreferredName(e.target.value)}
          placeholder="Peggy" />

        <div className="slot-top">
          <div>
            <label htmlFor="r-dob">Date of birth</label>
            <input id="r-dob" type="date" required value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
          </div>
          <div className="narrow">
            <label htmlFor="r-room">Room</label>
            <input id="r-room" value={room} onChange={(e) => setRoom(e.target.value)} />
          </div>
          <div>
            <label htmlFor="r-nhs">NHS number</label>
            <input id="r-nhs" inputMode="numeric" value={nhsNumber} onChange={(e) => setNhsNumber(e.target.value)}
              placeholder="943 476 5919" />
          </div>
        </div>

        <div className="slot-top">
          <div>
            <label htmlFor="r-gp">GP practice</label>
            <input id="r-gp" value={gpPractice} onChange={(e) => setGpPractice(e.target.value)} />
          </div>
          <div>
            <label htmlFor="r-gp-phone">GP phone</label>
            <input id="r-gp-phone" inputMode="tel" value={gpPhone} onChange={(e) => setGpPhone(e.target.value)} />
          </div>
          <div className="narrow">
            <label htmlFor="r-admitted">Admitted</label>
            <input id="r-admitted" type="date" value={admittedOn} onChange={(e) => setAdmittedOn(e.target.value)} />
          </div>
        </div>

        <label htmlFor="r-allergies">Allergies</label>
        <input id="r-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)}
          placeholder="Penicillin, Ibuprofen" />
        <p className="muted small">
          Separate them with commas. These are checked against every medicine before it reaches the round,
          so copy them exactly as the GP record has them.
        </p>

        {error ? <p className="error" role="alert">{error}</p> : null}

        {sameName ? (
          <div className="row-actions">
            <button type="button" disabled={busy} onClick={() => void submit(true)}>
              I have checked — this is a different person
            </button>
            <button type="button" className="quiet" onClick={onClose}>Stop and check</button>
          </div>
        ) : (
          <div className="row-actions">
            <button type="submit" disabled={busy}>
              {busy ? 'Saving…' : existing ? 'Save the changes' : 'Add them'}
            </button>
            <button type="button" className="quiet" onClick={onClose}>Cancel</button>
          </div>
        )}
      </form>
    </Overlay>
  );
}

/* ---------- Into hospital, back, left, died ---------- */

function StatusDialog({
  resident,
  medicines,
  onClose,
  onDone,
}: {
  resident: Full;
  medicines: { live: number; controlled: number };
  onClose: () => void;
  onDone: () => void;
}) {
  const [status, setStatus] = useState<Status>(resident.status === 'here' ? 'hospital' : 'here');
  const [note, setNote] = useState('');
  const [happenedOn, setHappenedOn] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const closing = status === 'left' || status === 'died';
  const name = `${resident.firstName} ${resident.lastName}`;

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/residents/${resident.id}/status`, {
          status,
          note,
          happenedOn: happenedOn || undefined,
        });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [resident.id, status, note, happenedOn, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{name}</h2>
        <p className="muted">They are recorded as {resident.statusLabel.toLowerCase()} at the moment.</p>

        <fieldset>
          <legend>What has happened</legend>
          {(['here', 'hospital', 'away', 'left', 'died'] as Status[])
            .filter((s) => s !== resident.status)
            .map((s) => (
              <label className="check" key={s}>
                <input type="radio" name="status" checked={status === s} onChange={() => { setStatus(s); setConfirm(''); }} />
                <span>
                  <strong>{STATUS_LABEL[s]}</strong>
                  {s === 'here' ? <span className="muted small"> — back on the round</span> : null}
                  {s === 'hospital' || s === 'away' ? (
                    <span className="muted small"> — doses paused, medicines kept</span>
                  ) : null}
                  {s === 'left' || s === 'died' ? (
                    <span className="muted small"> — medicines stopped, record closed</span>
                  ) : null}
                </span>
              </label>
            ))}
        </fieldset>

        {closing && medicines.live ? (
          <p className="notice warn">
            This stops {medicines.live} medicine{medicines.live === 1 ? '' : 's'} on their chart
            {medicines.controlled
              ? `, ${medicines.controlled} of them controlled drugs that will need two people to count them back to the pharmacy`
              : ''}
            . It cannot be undone here.
          </p>
        ) : null}

        <div className="slot-top">
          <div className="narrow">
            <label htmlFor="s-when">When</label>
            <input id="s-when" type="date" value={happenedOn} onChange={(e) => setHappenedOn(e.target.value)} />
          </div>
          <div>
            <label htmlFor="s-note">What happened</label>
            <input id="s-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={status === 'hospital' ? 'Fall, taken in by ambulance' : ''} />
          </div>
        </div>

        {status === 'died' ? (
          <>
            <label htmlFor="s-confirm">Type the word <strong>died</strong> to confirm</label>
            <input id="s-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
          </>
        ) : null}

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy || (status === 'died' && confirm.trim().toLowerCase() !== 'died')}>
            {busy ? 'Saving…' : 'Record it'}
          </button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function formatDate(date: string): string {
  const when = new Date(`${date}T12:00:00`);
  if (Number.isNaN(when.getTime())) return date;
  return when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatNhs(value: string): string {
  const digits = value.replace(/[^0-9]/g, '');
  return digits.length === 10 ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}` : value;
}
